import { TextareaHTMLAttributes, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { FormLabel } from "../FormLabel";
import ReactTooltip from "react-tooltip";
import FormikFieldError from "../FormikFieldError";
import { getInputClasses, getInputWrapperClasses } from "./inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";

export interface BaseTextAreaWithLabelProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
	className?: string;
	errorText?: string;
	flexGrow?: number;
	guideLine?: string;
	hasError?: boolean;
	hint?: string;
	label?: string;
	maxLength?: number;
	noMargin?: boolean;
	placeholder?: string;
	required?: boolean;
	resize?: 'none' | 'vertical' | 'horizontal' | 'both';
	rows?: number;
	showCharacterCount?: boolean;
	testId?: string;
	textAreaClassName?: string;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const TextAreaWithLabel: React.FC<BaseTextAreaWithLabelProps> = ({
	label,
	required,
	placeholder,
	className = "",
	textAreaClassName = "",
	errorText,
	hasError = false,
	guideLine,
	onChange,
	testId,
	flexGrow,
	noMargin,
	value,
	disabled,
	id,
	name,
	hint,
	maxLength,
	showCharacterCount = false,
	resize = 'vertical',
	rows = 4,
	...restProps
}) => {
	const [characterCount, setCharacterCount] = useState(value?.toString().length || 0);

	const hasInputError = !!errorText || hasError;

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newValue = e.currentTarget.value;
		setCharacterCount(newValue.length);

		if (onChange) {
			onChange(e);
		}
	};

	const remainingCharacters = maxLength ? maxLength - characterCount : 0;

	const resizeClass = {
		'none': 'resize-none',
		'vertical': 'resize-y',
		'horizontal': 'resize-x',
		'both': 'resize'
	}[resize];

	return (
		<div
			className={getInputWrapperClasses(hasInputError, noMargin, className)}
			style={{ flexGrow: flexGrow || 0 }}
		>
			{label && (
				<FormLabel
					htmlFor={id || name}
					label={label}
					required={required}
				>
					{guideLine && (
						<div className={"inline-block"}>
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}

			{hint && (
				<p className="-mt-2 mb-2 text-sm text-gray-500">{hint}</p>
			)}

			<div className="relative">
				<textarea
					id={id}
					name={name}
					data-testid={testId}
					placeholder={placeholder}
					onChange={handleChange}
					className={getInputClasses(hasInputError, `${resizeClass} ${textAreaClassName}`)}
					style={{
						height: 'auto',
						minHeight: `${rows * 1.5 + 2}rem`,
					}}
					value={value}
					disabled={disabled}
					maxLength={maxLength}
					rows={rows}
					{...restProps}
				/>

				{showCharacterCount && maxLength && (
					<div className="absolute bottom-3 right-3 text-xs text-gray-500 bg-white px-1">
						{remainingCharacters} character{remainingCharacters !== 1 ? 's' : ''} remaining
					</div>
				)}
			</div>

			<FormikFieldError text={errorText} />
		</div>
	);
};
